import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import Reveal from '../components/Reveal';
import { images, homeIntro, homeParagraphs, homeStats, videos, company } from '../mock';
import heroImg from '../assets/whitemoon-hero.jpg';

const Home = () => {
  return (
    <div>
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] w-full overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Aerial view of the Whitemoon Magnesite Deposit" className="w-full h-full object-cover kenburns" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-black/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
        </div>
        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-center">
          <Reveal>
            <div className="eyebrow text-amber-400 mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-amber-400" /> An American Magnesium Solution
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-display font-bold text-white text-5xl md:text-7xl lg:text-8xl tracking-[0.06em] leading-[0.95]">
              WHITEMOON
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p className="mt-8 max-w-2xl text-neutral-200 text-lg md:text-xl leading-relaxed font-light">
              High-grade, high-purity magnesite from the American Southwest - restoring domestic supply security for a critical mineral.
            </p>
          </Reveal>
          <Reveal delay={360}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/project"
                className="group inline-flex items-center gap-2 bg-white text-neutral-900 px-8 py-4 eyebrow hover:bg-amber-500 hover:text-white transition-all duration-300"
              >
                Explore the Project
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                to="/magnesium"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 eyebrow hover:bg-white/10 transition-all duration-300"
              >
                Why Magnesium
              </Link>
            </div>
          </Reveal>
          <div className="absolute bottom-10 left-6 lg:left-10 flex items-center gap-2 text-white/70">
            <MapPin className="w-4 h-4" />
            <span className="eyebrow text-[0.6rem]">{company.location}</span>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-28 md:py-36 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="eyebrow text-amber-700 mb-5">01 - The Company</div>
              <h2 className="font-display text-3xl md:text-4xl font-medium leading-tight text-neutral-900">
                A domestic source for a global-critical metal.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={120}>
              <p className="text-xl md:text-2xl text-neutral-900 leading-relaxed font-light">{homeIntro}</p>
            </Reveal>
            <div className="mt-10 grid md:grid-cols-2 gap-x-12 gap-y-6">
              {homeParagraphs.map((p, i) => (
                <Reveal key={i} delay={i * 90}>
                  <p className="text-neutral-600 leading-relaxed border-t border-neutral-200 pt-5">{p}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-neutral-950 text-white py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-y-14 gap-x-8">
          {homeStats.map((s, i) => (
            <Reveal key={i} delay={i * 90} className="text-center lg:text-left">
              <div className="font-display text-5xl md:text-6xl font-bold text-white">{s.value}</div>
              <div className="mt-3 text-neutral-400 text-sm max-w-[200px] mx-auto lg:mx-0">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURE SPLIT */}
      <section className="py-28 md:py-36 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal className="relative">
            <div className="overflow-hidden">
              <img src={images.mineral} alt="High-purity magnesite" className="w-full h-[520px] object-cover hover:scale-105 transition-transform duration-1000" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-white border border-neutral-200 px-8 py-6">
              <div className="font-display text-4xl font-bold">46.3%</div>
              <div className="eyebrow text-neutral-500 mt-1 text-[0.6rem]">Peak MgO grade</div>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <div className="eyebrow text-amber-700 mb-5">02 - The Deposit</div>
              <h2 className="font-display text-3xl md:text-5xl font-medium leading-tight text-neutral-900">
                The historic Whitemoon Magnesite Deposit.
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="mt-8 text-neutral-600 leading-relaxed text-lg">
                Visible magnesite at surface, open at depth, and classified as unusually pure with over 1M tons exposed and additional resources expected below.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <Link to="/project" className="mt-10 group inline-flex items-center gap-2 eyebrow text-neutral-900 border-b-2 border-amber-700 pb-1 hover:gap-4 transition-all duration-300">
                View project details <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VIDEO */}
      <section className="py-28 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow text-amber-700 mb-4">03 - On Site</div>
            <h2 className="font-display text-3xl md:text-5xl font-medium text-neutral-900 mb-14">From the field.</h2>
          </Reveal>
          <Reveal delay={120}>
            <div className="relative w-full overflow-hidden bg-black shadow-2xl">
              <video
                src={(process.env.PUBLIC_URL || '') + videos[0].src}
                poster={videos[0].posterUrl}
                controls
                playsInline
                preload="metadata"
                className="w-full aspect-video bg-black"
              />
            </div>
            <p className="mt-4 text-neutral-500 text-sm">{videos[0].subtitle}</p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 overflow-hidden">
        <img src={images.desertMountain} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-neutral-950/80" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl font-medium text-white leading-tight">
              Advancing near-term development on US soil.
            </h2>
            <Link to="/contact" className="mt-10 inline-flex items-center gap-2 bg-amber-500 text-white px-10 py-4 eyebrow hover:bg-amber-600 transition-colors duration-300">
              Get in touch <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
